<template>
  <component :is="props.tooltip && props.tooltip.length ? ZtTooltip : useFragment" :content="props.tooltip" placement="top">
    <component :is="buttonType.type" class="button-base" :class="getClass()" :data-testid="dataTestid" :disabled="props.disabled" v-bind="{ ...$attrs, ...buttonType.attrs }">
      <Icon v-if="icon && icon.length && !loading" :name="icon" :size="iconSize" />
      <Icon v-if="loading" name="general.spinner" :size="iconSize" />
      <span v-if="hasText" class="whitespace-nowrap" data-testid="button-text">{{ text }}<slot /></span>
      <BetaBadge v-if="isBeta" class="absolute -top-1 -right-1" size="regular" />
      <NewBadge v-else-if="isNew" class="absolute -top-1 -right-1" size="regular" />
    </component>
  </component>
</template>

<script lang="ts" setup>
import { RouteLocationRaw, RouterLink } from 'vue-router';
import { computed, useSlots } from 'vue';
import { Icon } from './../icon';
import { ZtTooltip } from '../tooltip';
import { BetaBadge } from './../utilities';
import { NewBadge } from './../utilities';
import { useFragment } from '../composables/useFragment';

const slots = useSlots();

interface Props {
  type?: 'solid' | 'ghost' | 'link';
  variant?: 'black' | 'gray' | 'blue' | 'secondary-blue' | 'green' | 'yellow' | 'orange' | 'red' | 'magenta' | 'pink-gradient' | 'ai';
  disabled?: boolean;
  icon?: string;
  to?: RouteLocationRaw;
  text?: string;
  submit?: boolean;
  loading?: boolean;
  tooltip?: string;
  dataTestid?: string;
  size?: 'regular' | 'medium' | 'large';
  iconSize?: 'xs' | 'sm' | 'base' | 'lg';
  isBeta?: boolean;
  isNew?: boolean;
}

const props = withDefaults(defineProps<Props>(), { type: 'solid', size: 'regular', variant: 'blue', iconSize: 'sm' });
const hasText = computed(() => !!slots.default || (props.text && props.text.length));

const buttonType = computed(() => {
  if (props.submit) {
    return {
      type: 'button',
      attrs: { type: 'submit' },
    };
  }

  if (props.to) {
    return {
      type: RouterLink,
      attrs: { to: props.to },
    };
  }

  return { type: 'a' };
});

const getClass = () => {
  const toReturn: any = {};

  toReturn[props.type] = true;
  toReturn[props.variant] = true;
  toReturn['disabled'] = props.disabled;
  toReturn['loading'] = props.loading;
  toReturn[props.size] = true;

  return toReturn;
};
</script>

<style scoped>
.button-base {
  @apply relative cursor-pointer w-max flex items-center justify-center gap-1.5 border border-transparent font-semibold leading-4 outline-none focus-visible:ring select-none;
}

.button-base.regular {
  @apply h-9 px-2 sm:px-2.5 py-0.5 text-xs sm:text-sm rounded-10;
}

.button-base.medium {
  @apply h-10 px-2 sm:px-2.5 py-0.5 text-xs sm:text-sm rounded-10;
}

.button-base.large {
  @apply h-12 px-4 py-0.5 text-sm sm:text-base rounded-[0.875rem];
}

.button-base.disabled {
  @apply cursor-not-allowed opacity-50 pointer-events-none;
}

.solid.black {
  @apply text-white bg-black dark:bg-zinc-900 hover:bg-slate-800 dark:hover:bg-zinc-800 active:bg-black focus-visible:ring-blue-500/50;
}
.solid.gray {
  @apply text-black dark:text-zinc-200 bg-slate-100 dark:bg-zinc-700 hover:bg-slate-200 dark:hover:bg-zinc-600 active:bg-black active:text-white dark:active:bg-zinc-200 dark:active:text-black focus:ring-blue-500/50;
}
.solid.blue {
  @apply text-white bg-blue-500 dark:bg-blue-600 hover:bg-blue-600 dark:hover:bg-blue-700 active:bg-blue-800 focus-visible:ring-blue-500/50;
}
.solid.secondary-blue {
  @apply text-blue-600 dark:text-blue-300 bg-blue-100 dark:bg-blue-900 border-blue-200 dark:border-blue-700 hover:bg-blue-200 dark:hover:bg-blue-800 hover:border-blue-300 dark:hover:border-blue-600 active:bg-blue-300 dark:active:bg-blue-700 active:border-blue-400 dark:active:border-blue-500 focus-visible:ring-blue-500/50;
}
.solid.green {
  @apply text-white bg-green-500 dark:bg-green-600 hover:bg-green-600 dark:hover:bg-green-700 active:bg-green-800 focus-visible:ring-green-500/50;
}
.solid.yellow {
  @apply text-white bg-yellow-500 dark:bg-yellow-600 hover:bg-yellow-600 dark:hover:bg-yellow-700 active:bg-yellow-800 focus-visible:ring-yellow-500/50;
}
.solid.orange {
  @apply text-white bg-gradient-to-tr from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 focus-visible:ring-red-500/50;
}
.solid.red {
  @apply text-white bg-red-500 dark:bg-red-600 hover:bg-red-600 dark:hover:bg-red-700 active:bg-red-800 focus-visible:ring-red-500/50;
}
.solid.magenta {
  @apply text-white bg-magenta-500 dark:bg-magenta-600 hover:bg-magenta-600 dark:hover:bg-magenta-700 active:bg-magenta-800 focus-visible:ring-magenta-500/50;
}
.solid.pink-gradient {
  @apply relative z-10 dark:text-black;
}
.solid.pink-gradient:hover::before {
  @apply absolute content-[''] inset-0 z-[-1] rounded-10;
  background: rgba(255, 255, 255, 0.4) !important;
}
.solid.pink-gradient::after {
  @apply absolute content-[''] inset-0 z-[-2] rounded-10;
  height: 100%;
  width: 100%;
  background: linear-gradient(to left, #f9f0e3, #f7ebe9, #f3e6f2, #f2e4f6, #efdffd, #f2e4f6, #f3e6f2, #f7ebe9, #f9f0e3);
  background-size: 200% 200%;
  animation: animateGlow 1.25s linear infinite;
}
@keyframes animateGlow {
  0% {
    background-position: 200% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}
.solid.ai {
  @apply text-zinc-900 dark:text-white border-[#EBDDE2] dark:border-[#EBDDE2]/40;
  background: linear-gradient(0deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.9) 100%), linear-gradient(90deg, #f5d9b7 3.72%, #e0adff 95.74%);
}
.solid.ai:hover {
  background: linear-gradient(0deg, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0.7) 100%), linear-gradient(90deg, #f5d9b7 3.72%, #e0adff 95.74%);
}
.solid.ai:active {
  @apply scale-[0.99];
}
.dark .solid.ai {
  background: linear-gradient(90deg, rgba(245, 217, 183, 0.3) 3.72%, rgba(224, 173, 255, 0.3) 95.74%);
}
.dark .solid.ai:hover {
  background: linear-gradient(90deg, rgba(245, 217, 183, 0.5) 3.72%, rgba(224, 173, 255, 0.5) 95.74%);
}

.ghost.black {
  @apply border-black text-black dark:text-zinc-300 dark:border-zinc-700 bg-transparent hover:bg-slate-200 dark:hover:bg-zinc-700 active:bg-black active:text-white focus:ring-blue-500/50;
}
.ghost.gray {
  @apply border-slate-500 text-slate-500 dark:text-zinc-300 dark:border-zinc-700 bg-transparent hover:bg-slate-200 dark:hover:bg-zinc-700 active:bg-black active:text-white focus:ring-blue-500/50;
}
.ghost.blue {
  @apply text-blue-500 dark:text-blue-600 border-blue-500 dark:border-blue-600 bg-transparent hover:bg-blue-100 hover:border-blue-600 dark:hover:bg-blue-700 dark:hover:border-blue-800 active:border-blue-800 active:text-blue-800 focus:ring-blue-500/50;
}
.ghost.green {
  @apply text-green-500 dark:text-green-600 border-green-500 dark:border-green-600 bg-transparent hover:bg-green-100 hover:border-green-600 dark:hover:bg-green-700 dark:hover:border-green-800 active:border-green-800 active:text-green-800 focus:ring-green-500/50;
}
.ghost.yellow {
  @apply text-yellow-500 dark:text-yellow-600 border-yellow-500 dark:border-yellow-600 bg-transparent hover:bg-yellow-100 hover:border-yellow-600 dark:hover:bg-yellow-700 dark:hover:border-yellow-800 active:border-yellow-800 active:text-yellow-800 focus:ring-yellow-500/50;
}
.ghost.orange {
  @apply text-orange-500 dark:text-orange-600 border-orange-500 dark:border-orange-600 bg-transparent hover:bg-orange-100 hover:border-orange-600 dark:hover:bg-orange-700 dark:hover:border-orange-800 active:border-orange-800 active:text-orange-800 focus:ring-orange-500/50;
}
.ghost.red {
  @apply text-red-500 dark:text-red-600 border-red-500 dark:border-red-600 bg-transparent hover:bg-red-100 hover:border-red-600 dark:hover:bg-red-700 dark:hover:border-red-800 active:border-red-800 active:text-red-800 focus:ring-red-500/50;
}
.ghost.magenta {
  @apply text-magenta-500 dark:text-magenta-600 border-magenta-500 dark:border-magenta-600 bg-transparent hover:bg-magenta-100 hover:border-magenta-600 dark:hover:bg-magenta-700 dark:hover:border-magenta-800 active:border-magenta-800 active:text-magenta-800 focus:ring-magenta-500/50;
}
.ghost.pink-gradient {
  background: linear-gradient(90deg, #aa5ada 14.33%, #d9a567 95.74%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  -moz-background-clip: text;
  -moz-text-fill-color: transparent;
  border-color: #aa5ada;
  color: #aa5ada;
}
.ghost.pink-gradient:hover {
  background: #fbf0e3;
  color: #aa5ada;
  -webkit-background-clip: border-box;
  -webkit-text-fill-color: #aa5ada;
  -moz-background-clip: border-box;
  -moz-text-fill-color: #aa5ada;
}

.link.black {
  @apply bg-transparent text-black dark:text-zinc-300 hover:bg-slate-200 dark:hover:bg-zinc-700 active:bg-black active:text-white focus-visible:ring-blue-500/50;
}
.link.gray {
  @apply bg-transparent text-slate-500 dark:text-zinc-300 hover:bg-slate-200 dark:hover:bg-zinc-700 active:bg-black active:text-white focus-visible:ring-blue-500/50;
}
.link.blue {
  @apply text-blue-500 dark:text-blue-600 border-transparent bg-transparent hover:bg-blue-100  dark:hover:bg-blue-700 active:bg-blue-100 active:text-blue-800 focus:ring-blue-500/50;
}
.link.green {
  @apply text-green-500 dark:text-green-600 border-transparent bg-transparent hover:bg-green-100  dark:hover:bg-green-700 active:bg-green-100 active:text-green-800 focus:ring-green-500/50;
}
.link.yellow {
  @apply text-yellow-500 dark:text-yellow-600 border-transparent bg-transparent hover:bg-yellow-100  dark:hover:bg-yellow-700 active:bg-yellow-100 active:text-yellow-800 focus:ring-yellow-500/50;
}
.link.orange {
  @apply text-orange-500 dark:text-orange-600 border-transparent bg-transparent hover:bg-orange-100  dark:hover:bg-orange-700 active:bg-orange-100 active:text-orange-800 focus:ring-orange-500/50;
}
.link.red {
  @apply text-red-500 dark:text-red-600 border-transparent bg-transparent hover:bg-red-100  dark:hover:bg-red-700 active:bg-red-100 active:text-red-800 focus:ring-red-500/50;
}
.link.magenta {
  @apply text-magenta-500 dark:text-magenta-600 border-transparent bg-transparent hover:bg-magenta-100  dark:hover:bg-magenta-700 active:bg-magenta-100 active:text-magenta-800 focus:ring-magenta-500/50;
}
.link.pink-gradient {
  background: linear-gradient(90deg, #aa5ada 14.33%, #d9a567 95.74%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  -moz-background-clip: text;
  -moz-text-fill-color: transparent;
  color: #aa5ada;
}
.link.pink-gradient:hover {
  background: #fbf0e3;
  color: #aa5ada;
  -webkit-background-clip: border-box;
  -webkit-text-fill-color: #aa5ada;
  -moz-background-clip: border-box;
  -moz-text-fill-color: #aa5ada;
}
</style>
